import React, { useEffect } from "react";
import _ from "lodash";
import cx from 'classnames';

import { animated, useSpring, useTransition } from "@react-spring/web";
import styles from "./styles.module.scss";
import { SearchBar } from "src/components/input";
import { TabBar } from "src/components/tabbar";
import { AssetPlaceholder, CollectionPlaceholder, CollectionsBrowser, EntityBrowser, StreamPlaceholder, StreamsBrowser } from "./browser";
import { useDispatch } from "react-redux";
import { invalidateAssetsSearchResults, invalidateCollectionsSearchResults, invalidateStreamsSearchResults, searchAssets, searchCollections, searchStreams } from "src/redux/actions/search";
import { useSelector } from "react-redux";
import { USER_DEFINED } from "src/constants/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import { HubLayoutColumn } from "src/types/hub";
import { EdithubContext } from "..";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";

interface AddAssetsFromHubProps {
  style: any;
  width: number;
  height?: number;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  selectedEntity: any;
}

interface SelectEntityContextProps {
  selectedEntity: any;
  contentType: string;
}

export const SelectEntityContext = React.createContext<SelectEntityContextProps>({
  selectedEntity: null,
  contentType: ""
});


const SelectEntity: React.FC<AddAssetsFromHubProps> = ({
  style,
  width,
  height,
  onSelection,
  selectedEntity
}) => {

  const { rowItem, showEntityTree, persistedRowItem } = React.useContext(EdithubContext);

  const dispatch = useDispatch();
  const [query, setQuery] = React.useState<string | null>("");

  const { trackStreamSelectionViewed } = useStreamsAdoption();

  useEffect(() => {
    if(!showEntityTree){
      setQuery("");
      dispatch(invalidateStreamsSearchResults());
      dispatch(invalidateAssetsSearchResults());
      dispatch(invalidateCollectionsSearchResults());
    } else {
      if(_.get(rowItem[0],"section.entityType") === "STREAM"){
        trackStreamSelectionViewed();
      }
    }
  },[showEntityTree])

  const browserTransition = useTransition(!query, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(query, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  const contentType = rowItem[0]?.section?.entityType;

  return (
    <SelectEntityContext.Provider value={{ selectedEntity, contentType }}>
      <animated.div
        className={cx(styles.hubAssets)}
        style={{ height: `${height}vh`, ...style }}
      >
        <div className={styles.searchBar}>
          <SearchBar query={query} onSearch={handleQueryChange} />
        </div>
        <div className={cx(styles.stacks, styles.content)}>
          {browserTransition(
            (style, item) =>
              item && (
                <AssetBrowser
                  style={style}
                  width={width}
                  onSelection={onSelection}
                />
              ),
          )}
          {searchTransition(
            (style, item) =>
              item && (
                <SearchResults
                  style={style}
                  width={width}
                  query={query}
                  onSelection={onSelection}
                />
              ),
          )}
        </div>
      </animated.div>
    </SelectEntityContext.Provider>
  );
};

interface AssetBrowserProps {
  style: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const AssetBrowser: React.FC<AssetBrowserProps> = ({ style, width, onSelection }) => {
  const { contentType } = React.useContext(SelectEntityContext);
  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
    <animated.div
      style={style}
      className={cx(styles.assetBrowser, styles.stack)}
    >
      <div
        className={cx(styles.stacks, styles.tabContainer)}
        style={{ width: width }}
      >
        {contentType !== "COLLECTION" && <StreamsBrowser
          width={width}
          action={(entity) => {
            return false;
          }}
          onSelection={handleSelection}
        />}
        {contentType === "COLLECTION" &&
          <CollectionsBrowser
            width={width}
            action={(entity) => {
              return false;
            }}
            onSelection={handleSelection}
          />}
      </div>
    </animated.div>
  );
}

interface SearchResultsProps {
  style: any;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ style, width, query, onSelection }) => {
  const { contentType } = React.useContext(SelectEntityContext);

  const { streams, collections, assets } = useSelector((state: any) => {
    const streamState = _.get(state, 'search.streams');
    const collectionState = _.get(state, 'search.collections');
    const assetState = _.get(state, 'search.assets');

    const streams = _.get(streamState, 'pagination.totalRecordsCount', 0);
    const collections = _.get(collectionState, 'pagination.totalRecordsCount', 0);
    const assets = _.get(assetState, 'pagination.totalRecordsCount', 0);

    return {
      streams: streams,
      collections: collections,
      assets: assets
    };
  }, _.isEqual);

  const [activeTab, setActiveTab] = React.useState(0);

  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
    <animated.div style={style} className={cx(styles.searchResults, styles.stack)}>
      <div className={cx(styles.stacks, styles.tabContainer)}>

        {contentType === "ASSET" && <AssetSearchResult query={query} width={width - 60}
          action={entity => {
            if (entity.entityType === 'asset') {
              return (
                <button className={styles.action}>
                  <i className={cx(styles.icon, 'paperflite-add')}></i>
                </button>
              );
            }

            return false;
          }}
          onSelection={handleSelection}
        />}

        {contentType === "STREAM" && <StreamSearchResult query={query} width={width}
          action={entity => {
            return false;
          }}
          onSelection={handleSelection}
        />}
        {contentType === "COLLECTION" && <CollectionSearchResult query={query} width={width}
          action={entity => {
            return false;
          }}
          onSelection={handleSelection}
        />}
      </div>
    </animated.div>
  );
}


interface CollectionSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const CollectionSearchResult: React.FC<CollectionSearchResultProps> = ({ className, query, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'search.collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.type === USER_DEFINED;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchCollectionForPage(0);
  }, []);

  const searchCollectionForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if(page <= 0) {
        dispatch(invalidateCollectionsSearchResults());
      }
      dispatch(searchCollections(request));
    }
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      searchCollectionForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
          loading={loading}
        />
      </InfiniteScroll>
    </div>
  );
}

interface StreamSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const StreamSearchResult: React.FC<StreamSearchResultProps> = ({ className, query, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, streams, nextPage, hasMoreItems } = useSelector((state: any) => {
    const streamState = _.get(state, 'search.streams');
    const loading = _.get(streamState, 'loading');
    const pagination = _.get(streamState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const streams = (_.get(streamState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      streams: streams,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchStreamForPage(0);
  }, []);

  const searchStreamForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if(page <= 0) {
      dispatch(invalidateStreamsSearchResults());
      }
      dispatch(searchStreams(request));
    }
  }

  const handleLoadMoreStreams = () => {
    if (nextPage) {
      searchStreamForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreStreams}
        loader={() => {
          if (_.size(streams) === 0) {
            return (<>
              <StreamPlaceholder width={'50%'} />
              <StreamPlaceholder width={'70%'} />
              <StreamPlaceholder width={'60%'} />
              <StreamPlaceholder width={'50%'} />
            </>)
          } else {
            return <StreamPlaceholder width={'70%'} />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={streams}
          action={action}
          onSelection={onSelection}
          loading={loading}
        />
      </InfiniteScroll>
    </div>
  );
}

interface AssetSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const AssetSearchResult: React.FC<AssetSearchResultProps> = ({ className, query, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, assets, nextPage, hasMoreItems } = useSelector((state: any) => {
    const assetState = _.get(state, 'search.assets');
    const loading = _.get(assetState, 'loading');
    const pagination = _.get(assetState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const assets = (_.get(assetState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      assets: assets,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchAssetForPage(0);
  }, []);

  const searchAssetForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if(page <= 0) {
      dispatch(invalidateAssetsSearchResults());
      }
      dispatch(searchAssets(request));
    }
  }

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      searchAssetForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreAssets}
        loader={() => {
          if (_.size(assets) === 0) {
            return (<>
              <AssetPlaceholder />
              <AssetPlaceholder />
              <AssetPlaceholder />
              <AssetPlaceholder />
            </>)
          } else {
            return <AssetPlaceholder />
          }
        }}
      >
        <EntityBrowser className={className} 
          width={width} 
          items={assets} 
          action={action} 
          onSelection={onSelection} 
          loading={loading}
        />
      </InfiniteScroll>
    </div>
  );
}


export { SelectEntity };